<script setup lang="ts">
/**
 * Поле «Сервисы» в мета-панели: состав фича-стенда задачи (макет 272:2913).
 *
 * Правится на месте, как остальные поля панели: список уходит на сервер сразу
 * после правки. Отдельная кнопка «Сохранить» здесь была бы единственной на всю
 * колонку.
 *
 * Подсказки (репозитории, где ветка задачи уже есть) приходят той же ручкой и
 * показываются строкой под чипами — в состав они идут только по нажатию.
 */
import { computed, onMounted, ref, watch } from 'vue'

import IssueServicesPicker from '@/features/devops/components/IssueServicesPicker.vue'
import { listIssueServices, setIssueServices, type IssueService } from '@/features/devops/api'

const props = defineProps<{ issueId: string }>()

const emit = defineEmits<{ failed: [error: unknown] }>()

const services = ref<IssueService[]>([])

const picked = computed(() =>
  services.value.filter((service) => !service.suggested).map((service) => service.projectId),
)

const suggestions = computed(() =>
  services.value
    .filter((service) => service.suggested)
    .map((service) => ({ projectId: service.projectId, path: service.path })),
)

const load = async () => {
  if (!props.issueId) return

  try {
    services.value = await listIssueServices(props.issueId)
  } catch (failure) {
    // Молча: без состава карточка остаётся рабочей, а всплывающая ошибка при
    // каждом открытии задачи мешала бы читать её саму.
    services.value = []
    void failure
  }
}

const save = async (ids: string[]) => {
  const previous = services.value

  services.value = [
    ...ids.map((projectId) => ({
      projectId,
      path: pathOf(projectId),
      suggested: false,
    })),
    ...services.value.filter(
      (service) => service.suggested && !ids.includes(service.projectId),
    ),
  ]

  try {
    await setIssueServices(props.issueId, ids)
    await load()
  } catch (failure) {
    services.value = previous
    emit('failed', failure)
  }
}

/** Путь уже известного сервиса: список выбора отдаёт только идентификаторы. */
const pathOf = (projectId: string) =>
  services.value.find((service) => service.projectId === projectId)?.path ?? ''

onMounted(load)

watch(() => props.issueId, load)
</script>

<template>
  <IssueServicesPicker
    :model-value="picked"
    :suggestions="suggestions"
    @update:model-value="save"
  />
</template>
